import api from '../services/api';

/**
 * External Data Model (EDAMS Database)
 * Handles all API calls for external EDAMS data (read-only)
 * 
 * This includes:
 * - Job Cards
 * - Customer Data
 * - Property Data
 * - Employee Data
 * - Applications
 */

// ============================================================================
// Job Cards (EDAMS)
// ============================================================================

/**
 * Get all job cards from EDAMS with pagination and filtering
 */
export const getJobCards = async (filters = {}) => {
  try {
    const response = await api.get('/api/external/jobcards', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching job cards:', error);
    throw new Error('Failed to fetch job cards: ' + error.message);
  }
};

/**
 * Get a single job card by jobcard number
 */
export const getJobCard = async (jobCardNo) => {
  try {
    const response = await api.get(`/api/external/jobcards/${jobCardNo}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching job card:', error);
    throw new Error('Failed to fetch job card: ' + error.message);
  }
};

/**
 * Get job card events/history from EDAMS
 */
export const getJobCardEvents = async (applicationId) => {
  try {
    const response = await api.get(`/api/external/jobcards/${applicationId}/events`);
    return response.data;
  } catch (error) {
    console.error('Error fetching job card events:', error);
    throw new Error('Failed to fetch job card events: ' + error.message);
  }
};

/**
 * Get job card statistics from EDAMS
 */
export const getJobCardStats = async (startDate = null) => {
  try {
    const params = {};
    if (startDate) {
      params.start_date = startDate;
    }
    const response = await api.get('/api/external/jobcards/stats', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching job card stats:', error);
    throw new Error('Failed to fetch job card statistics: ' + error.message);
  }
};

/**
 * Update job card status in EDAMS
 */
export const updateJobCardStatus = async (applicationId, status) => {
  try {
    const response = await api.put(`/api/external/jobcards/${applicationId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating job card status:', error);
    throw new Error('Failed to update job card status: ' + error.message);
  }
};

// ============================================================================
// Employees (EDAMS)
// ============================================================================

/**
 * Get all employees from EDAMS with pagination and filtering
 * These are read-only from EDAMS - used to create local artisan accounts
 */
export const getEdamsEmployees = async (filters = {}) => {
  try {
    const response = await api.get('/api/external/employees', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching EDAMS employees:', error);
    throw new Error('Failed to fetch EDAMS employees: ' + error.message);
  }
};

/**
 * Get a single employee from EDAMS by employee number
 */
export const getEdamsEmployee = async (employeeNo) => {
  try {
    const response = await api.get(`/api/external/employees/${employeeNo}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching EDAMS employee:', error);
    throw new Error('Failed to fetch EDAMS employee: ' + error.message);
  }
};

// ============================================================================
// Worker Profile & Performance (EDAMS)
// ============================================================================

/**
 * Get comprehensive worker profile from EDAMS
 */
export const getWorkerProfile = async (emplNo) => {
  try {
    // Encode the parameter to handle spaces and special characters
    const encodedEmplNo = encodeURIComponent(emplNo);
    const response = await api.get(`/api/mms/workers/${encodedEmplNo}/profile`);
    return response.data;
  } catch (error) {
    console.error('Error fetching worker profile:', error);
    throw new Error('Failed to fetch worker profile: ' + error.message);
  }
};

/**
 * Get worker performance metrics
 */
export const getWorkerPerformance = async (emplNo, months = 12) => {
  try {
    const response = await api.get(`/api/mms/workers/${emplNo}/performance`, {
      params: { months }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching worker performance:', error);
    throw new Error('Failed to fetch worker performance: ' + error.message);
  }
};

/**
 * Get worker job history
 */
export const getWorkerHistory = async (emplNo, limit = 50) => {
  try {
    const response = await api.get(`/api/mms/workers/${emplNo}/history`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching worker history:', error);
    throw new Error('Failed to fetch worker history: ' + error.message);
  }
};

/**
 * Get worker skills breakdown
 */
export const getWorkerSkills = async (emplNo, months = 12) => {
  try {
    const response = await api.get(`/api/mms/workers/${emplNo}/skills`, {
      params: { months }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching worker skills:', error);
    throw new Error('Failed to fetch worker skills: ' + error.message);
  }
};

/**
 * Get raw coordinate data from EDAMS for a property
 */
export const getPropertyRawCoordinates = async (propertyId, applicationId = null) => {
  try {
    const params = applicationId ? { application_id: applicationId } : {};
    const response = await api.get(`/api/external/properties/${propertyId}/raw-coordinates`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching raw coordinates:', error);
    throw new Error('Failed to fetch raw coordinates: ' + error.message);
  }
};

export default {
  // Job Cards
  getJobCards,
  getJobCard,
  getJobCardEvents,
  getJobCardStats,
  updateJobCardStatus,
  // Employees
  getEdamsEmployees,
  getEdamsEmployee,
  // Properties
  getPropertyRawCoordinates,
  // Worker Profiles
  getWorkerProfile,
  getWorkerPerformance,
  getWorkerHistory,
  getWorkerSkills
};
